import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Crown, LogOut, Upload, Image as ImageIcon, Smartphone, FileArchive, Sparkles,
  Trash2, Plus, Save, Eye, EyeOff, KeyRound, Pencil, Radio,
} from "lucide-react";
import api, { resolveUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TABS = [
  { id: "general", label: "General" },
  { id: "media", label: "Media" },
  { id: "screenshots", label: "Screenshots" },
  { id: "howto", label: "How To" },
  { id: "markets", label: "Markets" },
  { id: "rates", label: "Game Rates" },
  { id: "notice", label: "Notice & Live" },
  { id: "security", label: "Security" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("general");
  const [settings, setSettings] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [rates, setRates] = useState([]);
  const [shots, setShots] = useState([]);
  const [howto, setHowto] = useState([]);

  const refresh = async () => {
    const [s, m, r, sc, h] = await Promise.all([
      api.get("/settings"),
      api.get("/markets"),
      api.get("/rates"),
      api.get("/screenshots"),
      api.get("/howto"),
    ]);
    setSettings(s.data); setMarkets(m.data); setRates(r.data);
    setShots(sc.data); setHowto(h.data);
  };

  useEffect(() => { refresh(); }, []);

  const handleLogout = () => { logout(); nav("/admin/login"); };

  if (!settings) return <div className="min-h-screen flex items-center justify-center text-gold">Loading…</div>;

  return (
    <div className="admin-shell min-h-screen relative" data-testid="admin-dashboard">
      <div className="absolute inset-0 crown-grid-bg pointer-events-none opacity-30" />
      <div className="relative z-10 max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl gold-gradient flex items-center justify-center">
              <Crown className="w-6 h-6 text-black" />
            </div>
            <div>
              <div className="label-gold">Admin Dashboard</div>
              <h1 className="font-display text-2xl font-extrabold gold-text-gradient">Royal Games Markets</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="btn-outline-gold text-sm !py-2" data-testid="view-site-button">
              <Eye className="w-4 h-4" /> View Site
            </a>
            <span className="text-xs text-[#b0b0b0] hidden sm:inline" data-testid="admin-email">{user?.email}</span>
            <button onClick={handleLogout} className="btn-red text-sm !py-2" data-testid="logout-button">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="glass rounded-2xl p-2 mb-6 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition ${
                  tab === t.id ? "gold-gradient text-black shadow-[0_0_20px_rgba(255,215,0,0.4)]" : "text-[#b0b0b0] hover:text-gold"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat label="Downloads" value={(settings.download_count || 0).toLocaleString()} icon={<Sparkles className="w-4 h-4" />} testid="stat-downloads" />
          <Stat label="Version" value={settings.version} icon={<FileArchive className="w-4 h-4" />} testid="stat-version" />
          <Stat label="Markets" value={markets.length} icon={<Smartphone className="w-4 h-4" />} testid="stat-markets" />
          <Stat label="Status" value={settings.download_enabled ? "Live" : "Off"} icon={<Radio className="w-4 h-4" />} testid="stat-status" />
        </div>

        {/* Tab content */}
        <div className="glass rounded-3xl p-6">
          {tab === "general" && <GeneralTab settings={settings} onSave={refresh} />}
          {tab === "media" && <MediaTab settings={settings} onChange={refresh} />}
          {tab === "screenshots" && <ScreenshotsTab shots={shots} onChange={refresh} />}
          {tab === "howto" && <HowToTab howto={howto} onChange={refresh} />}
          {tab === "markets" && <MarketsTab markets={markets} onChange={refresh} />}
          {tab === "rates" && <RatesTab rates={rates} onChange={refresh} />}
          {tab === "notice" && <NoticeTab settings={settings} onSave={refresh} />}
          {tab === "security" && <SecurityTab />}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon, testid }) {
  return (
    <div className="glass rounded-2xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-gold">{icon}<span className="label-gold">{label}</span></div>
      <div className="text-2xl font-bold text-white mt-2">{value}</div>
    </div>
  );
}

/* ---------------- General ---------------- */
function GeneralTab({ settings, onSave }) {
  const [f, setF] = useState({
    app_name: settings.app_name,
    version: settings.version,
    button_text: settings.button_text,
    welcome_text: settings.welcome_text,
    download_enabled: settings.download_enabled,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", f);
      toast.success("Settings saved");
      onSave();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const upd = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="space-y-5">
      <h3 className="font-display text-xl font-bold text-gold">General Settings</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="App Name"><input data-testid="general-app-name" className="input-gold" value={f.app_name} onChange={(e) => upd("app_name", e.target.value)} /></Field>
        <Field label="Version"><input data-testid="general-version" className="input-gold" value={f.version} onChange={(e) => upd("version", e.target.value)} /></Field>
        <Field label="Download Button Text"><input data-testid="general-button-text" className="input-gold" value={f.button_text} onChange={(e) => upd("button_text", e.target.value)} /></Field>
        <Field label="Download Enabled">
          <button
            onClick={() => upd("download_enabled", !f.download_enabled)}
            data-testid="toggle-download"
            className={`px-4 py-2 rounded-xl font-semibold ${f.download_enabled ? "gold-gradient text-black" : "glass-red text-white"}`}
          >
            {f.download_enabled ? <><Eye className="w-4 h-4 inline mr-1" /> Enabled</> : <><EyeOff className="w-4 h-4 inline mr-1" /> Disabled</>}
          </button>
        </Field>
        <Field label="Welcome Text" full>
          <textarea data-testid="general-welcome" className="input-gold min-h-[80px]" value={f.welcome_text} onChange={(e) => upd("welcome_text", e.target.value)} />
        </Field>
      </div>
      <button onClick={save} disabled={saving} className="btn-gold" data-testid="save-general-button">
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
      </button>
    </div>
  );
}

/* ---------------- Media ---------------- */
function MediaTab({ settings, onChange }) {
  const apkRef = useRef(); const logoRef = useRef(); const bannerRef = useRef();
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState(0);
  const upload = async (file, kind) => {
    if (!file) return;
    setBusy(kind);
    setProgress(0);
    try {
      const fd = new FormData();
      const cfg = {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
        timeout: 15 * 60 * 1000, // 15 min for large APKs
      };
      if (kind === "apk") {
        fd.append("file", file);
        await api.post("/upload/apk", fd, cfg);
      } else {
        fd.append("kind", kind);
        fd.append("file", file);
        await api.post("/upload/image", fd, cfg);
      }
      toast.success(`${kind} uploaded successfully`);
      onChange();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Upload failed";
      console.error("Upload error", e);
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setBusy("");
      setProgress(0);
    }
  };
  return (
    <div className="space-y-6">
      <h3 className="font-display text-xl font-bold text-gold">APK & Media Upload</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <UploadCard
          title="APK File"
          subtitle={settings.apk_url ? "✓ Uploaded" : "No file"}
          icon={<FileArchive className="w-6 h-6 text-gold" />}
          accept=".apk"
          inputRef={apkRef}
          loading={busy === "apk"}
          progress={busy === "apk" ? progress : 0}
          onPick={(f) => upload(f, "apk")}
          testid="upload-apk"
        />
        <UploadCard
          title="Logo"
          subtitle={settings.logo_url ? "Uploaded" : "Not set"}
          icon={<Crown className="w-6 h-6 text-gold" />}
          accept="image/*"
          inputRef={logoRef}
          loading={busy === "logo"}
          progress={busy === "logo" ? progress : 0}
          onPick={(f) => upload(f, "logo")}
          preview={settings.logo_url}
          testid="upload-logo"
        />
        <UploadCard
          title="Banner"
          subtitle={settings.banner_url ? "Uploaded" : "Not set"}
          icon={<ImageIcon className="w-6 h-6 text-gold" />}
          accept="image/*"
          inputRef={bannerRef}
          loading={busy === "banner"}
          progress={busy === "banner" ? progress : 0}
          onPick={(f) => upload(f, "banner")}
          preview={settings.banner_url}
          testid="upload-banner"
        />
      </div>
    </div>
  );
}

function UploadCard({ title, subtitle, icon, accept, inputRef, loading, progress, onPick, preview, testid }) {
  return (
    <div className="glass rounded-2xl p-4 flex flex-col gap-3" data-testid={testid}>
      <div className="flex items-center gap-2">{icon}<div className="font-display font-bold text-white">{title}</div></div>
      <div className="text-xs text-[#b0b0b0]">{subtitle}</div>
      {preview && <div className="aspect-video rounded-xl overflow-hidden bg-black/40"><img src={resolveUrl(preview)} alt={title} className="w-full h-full object-cover" /></div>}
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => onPick(e.target.files?.[0])} data-testid={`${testid}-input`} />
      {loading ? (
        <div className="space-y-2">
          <div className="h-2 rounded-full bg-black/50 overflow-hidden">
            <div className="h-full gold-gradient transition-all" style={{ width: `${progress || 0}%` }} />
          </div>
          <div className="text-xs text-gold text-center font-bold" data-testid={`${testid}-progress`}>
            {progress > 0 ? `${progress}% — uploading…` : "Starting…"}
          </div>
        </div>
      ) : (
        <button onClick={() => inputRef.current.click()} className="btn-outline-gold !py-2 text-sm" data-testid={`${testid}-button`}>
          <Upload className="w-4 h-4" /> Upload
        </button>
      )}
    </div>
  );
}

/* ---------------- Screenshots ---------------- */
function ScreenshotsTab({ shots, onChange }) {
  const ref = useRef();
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("kind", "screenshot"); fd.append("file", file);
    try { await api.post("/upload/image", fd); toast.success("Screenshot added"); onChange(); }
    catch { toast.error("Upload failed"); }
  };
  const del = async (id) => { await api.delete(`/screenshots/${id}`); toast.success("Deleted"); onChange(); };
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold text-gold">App Screenshots</h3>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="screenshot-input" />
        <button onClick={() => ref.current.click()} className="btn-gold !py-2 !px-4 text-sm" data-testid="add-screenshot-button"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {shots.map((s) => (
          <div key={s.id} className="relative glass rounded-2xl overflow-hidden aspect-[9/16] group">
            <img src={resolveUrl(s.url)} alt="ss" className="w-full h-full object-cover" />
            <button onClick={() => del(s.id)} className="absolute top-2 right-2 w-9 h-9 rounded-full glass-red flex items-center justify-center opacity-0 group-hover:opacity-100 transition" data-testid={`delete-shot-${s.id}`}>
              <Trash2 className="w-4 h-4 text-white" />
            </button>
          </div>
        ))}
        {shots.length === 0 && <div className="col-span-full text-center text-[#b0b0b0] py-10">No screenshots yet</div>}
      </div>
    </div>
  );
}

/* ---------------- HowTo ---------------- */
function HowToTab({ howto, onChange }) {
  const upload = async (step, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/upload/howto/${step}`, fd); toast.success(`Step ${step} updated`); onChange(); }
    catch { toast.error("Upload failed"); }
  };
  return (
    <div className="space-y-5">
      <h3 className="font-display text-xl font-bold text-gold">How To Download — 4 Step Images</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((s) => {
          const item = howto.find((h) => h.step === s);
          return (
            <div key={s} className="glass rounded-2xl p-3 space-y-2" data-testid={`howto-edit-${s}`}>
              <div className="font-display font-bold text-white text-center">Step {s}</div>
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-black/40 flex items-center justify-center">
                {item?.image_url ? <img src={resolveUrl(item.image_url)} alt={`step-${s}`} className="w-full h-full object-cover" /> : <span className="text-[#b0b0b0] text-xs">No image</span>}
              </div>
              <label className="btn-outline-gold !py-2 text-sm w-full justify-center cursor-pointer">
                <Upload className="w-4 h-4" /> Upload
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(s, e.target.files?.[0])} />
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Markets ---------------- */
function MarketsTab({ markets, onChange }) {
  const [draft, setDraft] = useState({ name: "", category: "day", time_label: "" });
  const add = async () => {
    if (!draft.name) return;
    await api.post("/markets", draft);
    setDraft({ name: "", category: "day", time_label: "" });
    toast.success("Market added"); onChange();
  };
  const update = async (m, patch) => {
    await api.put(`/markets/${m.id}`, { ...m, ...patch });
    onChange();
  };
  const del = async (id) => { await api.delete(`/markets/${id}`); toast.success("Deleted"); onChange(); };

  return (
    <div className="space-y-6">
      <h3 className="font-display text-xl font-bold text-gold">Manage Markets & Results</h3>
      <div className="glass rounded-2xl p-4 grid sm:grid-cols-4 gap-3" data-testid="add-market-form">
        <input className="input-gold" placeholder="Market name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="market-name-input" />
        <select className="input-gold" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} data-testid="market-category-select">
          <option value="day">Day</option>
          <option value="night">Night</option>
        </select>
        <input className="input-gold" placeholder="Time (optional)" value={draft.time_label} onChange={(e) => setDraft({ ...draft, time_label: e.target.value })} data-testid="market-time-input" />
        <button onClick={add} className="btn-gold !py-2" data-testid="add-market-button"><Plus className="w-4 h-4" /> Add</button>
      </div>

      {["day", "night"].map((cat) => (
        <div key={cat}>
          <div className="label-gold mb-2">{cat === "day" ? "Day Markets" : "Night Markets"}</div>
          <div className="space-y-3">
            {markets.filter((m) => m.category === cat).map((m) => (
              <div key={m.id} className="glass rounded-xl p-4 space-y-3" data-testid={`market-row-${m.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <input className="input-gold flex-1 min-w-[160px] font-bold" value={m.name} onChange={(e) => update(m, { name: e.target.value })} />
                  <input className="input-gold w-32" placeholder="Time" value={m.time_label || ""} onChange={(e) => update(m, { time_label: e.target.value })} />
                  <button onClick={() => update(m, { is_live: !m.is_live })} className={`px-3 py-2 rounded-xl text-xs font-bold ${m.is_live ? "glass-red text-white" : "btn-outline-gold !py-2 !px-3"}`} data-testid={`toggle-live-${m.id}`}>
                    {m.is_live ? "● LIVE" : "Mark Live"}
                  </button>
                  <button onClick={() => del(m.id)} className="w-9 h-9 rounded-xl glass-red flex items-center justify-center" data-testid={`delete-market-${m.id}`}>
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gold mb-1">Open Pana</div>
                    <input
                      className="input-gold text-center font-bold"
                      placeholder="e.g. 123"
                      defaultValue={m.open_result || ""}
                      onBlur={(e) => e.target.value !== (m.open_result || "") && update(m, { open_result: e.target.value })}
                      data-testid={`open-${m.id}`}
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-red-royal mb-1">Jodi</div>
                    <input
                      className="input-gold text-center font-bold"
                      placeholder="e.g. 45"
                      defaultValue={m.jodi_result || ""}
                      onBlur={(e) => e.target.value !== (m.jodi_result || "") && update(m, { jodi_result: e.target.value })}
                      data-testid={`jodi-${m.id}`}
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gold mb-1">Close Pana</div>
                    <input
                      className="input-gold text-center font-bold"
                      placeholder="e.g. 678"
                      defaultValue={m.close_result || ""}
                      onBlur={(e) => e.target.value !== (m.close_result || "") && update(m, { close_result: e.target.value })}
                      data-testid={`close-${m.id}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="text-xs text-[#b0b0b0]">Tip: Result fields save when you click outside the input.</div>
    </div>
  );
}

/* ---------------- Rates ---------------- */
function RatesTab({ rates, onChange }) {
  const update = async (r, payout) => {
    await api.put(`/rates/${r.id}`, { payout });
    onChange();
  };
  return (
    <div className="space-y-4">
      <h3 className="font-display text-xl font-bold text-gold">Edit Game Rates</h3>
      <div className="space-y-2">
        {rates.map((r) => (
          <div key={r.id} className="glass rounded-xl p-3 flex items-center gap-3" data-testid={`rate-edit-${r.id}`}>
            <div className="font-display font-bold text-white w-32 sm:w-40">{r.name}</div>
            <div className="text-[#b0b0b0] text-sm">{r.stake} →</div>
            <input
              className="input-gold flex-1"
              defaultValue={r.payout}
              onBlur={(e) => e.target.value !== r.payout && update(r, e.target.value)}
              data-testid={`rate-input-${r.id}`}
            />
            <Pencil className="w-4 h-4 text-gold opacity-50" />
          </div>
        ))}
      </div>
      <div className="text-xs text-[#b0b0b0]">Tip: changes save when you leave the field.</div>
    </div>
  );
}

/* ---------------- Notice & Live ---------------- */
function NoticeTab({ settings, onSave }) {
  // Attach stable client-side ids so React key stays consistent when items are added/removed
  const withIds = (arr) => (arr || []).map((text) => ({ id: crypto.randomUUID(), text }));
  const [msgs, setMsgs] = useState(() => withIds(settings.notice_messages));
  const [alert, setAlert] = useState(settings.live_alert || "");
  const [active, setActive] = useState(!!settings.live_alert_active);
  const [instructions, setInstructions] = useState(() => withIds(settings.instructions));
  const save = async () => {
    await api.put("/settings", {
      notice_messages: msgs.map((m) => m.text),
      live_alert: alert,
      live_alert_active: active,
      instructions: instructions.map((m) => m.text),
    });
    toast.success("Notice board updated"); onSave();
  };
  const upd = (id, v) => setMsgs(msgs.map((m) => (m.id === id ? { ...m, text: v } : m)));
  const del = (id) => setMsgs(msgs.filter((m) => m.id !== id));
  const add = () => setMsgs([...msgs, { id: crypto.randomUUID(), text: "" }]);
  const updI = (id, v) => setInstructions(instructions.map((m) => (m.id === id ? { ...m, text: v } : m)));
  const delI = (id) => setInstructions(instructions.filter((m) => m.id !== id));
  const addI = () => setInstructions([...instructions, { id: crypto.randomUUID(), text: "" }]);
  return (
    <div className="space-y-6">
      <h3 className="font-display text-xl font-bold text-gold">Notice Board, Live Result & Instructions</h3>

      <div className="glass-red rounded-2xl p-5 space-y-3" data-testid="live-alert-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2"><Radio className="w-5 h-5 text-red-royal" /><div className="font-display text-lg font-bold text-white">Live Result Highlight</div></div>
          <button onClick={() => setActive(!active)} className={`px-3 py-2 rounded-xl text-xs font-bold ${active ? "gold-gradient text-black" : "btn-outline-gold !py-2 !px-3"}`} data-testid="toggle-live-alert">
            {active ? "ACTIVE" : "Inactive"}
          </button>
        </div>
        <input
          className="input-gold"
          placeholder="e.g. KALYAN OPEN RESULT 5-6-9-127"
          value={alert}
          onChange={(e) => setAlert(e.target.value)}
          data-testid="live-alert-input"
        />
        <div className="text-xs text-[#ddd]">When active, this shows as a RED highlighted bar at the top of the notice board.</div>
      </div>

      <div className="space-y-3">
        <div className="label-gold">Scrolling Notices</div>
        {msgs.map((m, i) => (
          <div key={m.id} className="flex gap-2" data-testid={`notice-row-${i}`}>
            <input className="input-gold flex-1" value={m.text} onChange={(e) => upd(m.id, e.target.value)} />
            <button onClick={() => del(m.id)} className="w-11 h-11 rounded-xl glass-red flex items-center justify-center" data-testid={`delete-notice-${i}`}>
              <Trash2 className="w-4 h-4 text-white" />
            </button>
          </div>
        ))}
        <button onClick={add} className="btn-outline-gold !py-2 text-sm" data-testid="add-notice-button"><Plus className="w-4 h-4" /> Add Notice</button>
      </div>

      <div className="space-y-3 pt-2 border-t border-[#FFD700]/15">
        <div className="label-gold">Instruction Board (shown below notice board)</div>
        {instructions.map((m, i) => (
          <div key={m.id} className="flex gap-2" data-testid={`instruction-row-${i}`}>
            <input className="input-gold flex-1" placeholder="e.g. STEP 1: Click on Download APK" value={m.text} onChange={(e) => updI(m.id, e.target.value)} />
            <button onClick={() => delI(m.id)} className="w-11 h-11 rounded-xl glass-red flex items-center justify-center" data-testid={`delete-instruction-${i}`}>
              <Trash2 className="w-4 h-4 text-white" />
            </button>
          </div>
        ))}
        <button onClick={addI} className="btn-outline-gold !py-2 text-sm" data-testid="add-instruction-button"><Plus className="w-4 h-4" /> Add Instruction</button>
      </div>

      <button onClick={save} className="btn-gold" data-testid="save-notice-button"><Save className="w-4 h-4" /> Save All</button>
    </div>
  );
}

/* ---------------- Security ---------------- */
function SecurityTab() {
  const [f, setF] = useState({ old_password: "", new_password: "", new_email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password.length < 6) { toast.error("Password must be ≥ 6 chars"); return; }
    setBusy(true);
    try {
      const payload = { old_password: f.old_password, new_password: f.new_password };
      if (f.new_email) payload.new_email = f.new_email;
      await api.post("/auth/change-password", payload);
      toast.success("Credentials updated");
      setF({ old_password: "", new_password: "", new_email: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5 max-w-lg" data-testid="security-form">
      <h3 className="font-display text-xl font-bold text-gold flex items-center gap-2"><KeyRound className="w-5 h-5" /> Change Admin Credentials</h3>
      <Field label="Current Password"><input type="password" className="input-gold" required value={f.old_password} onChange={(e) => setF({ ...f, old_password: e.target.value })} data-testid="old-password-input" /></Field>
      <Field label="New Password"><input type="password" className="input-gold" required value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="new-password-input" /></Field>
      <Field label="New Email (optional)"><input type="email" className="input-gold" value={f.new_email} onChange={(e) => setF({ ...f, new_email: e.target.value })} data-testid="new-email-input" /></Field>
      <button type="submit" disabled={busy} className="btn-gold" data-testid="change-password-button"><Save className="w-4 h-4" /> {busy ? "Updating…" : "Update Credentials"}</button>
    </form>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="label-gold block mb-2">{label}</label>
      {children}
    </div>
  );
}
